import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Check, Inbox, Layers, RefreshCw, Sparkles } from "lucide-react";

import { apiGet, apiPost } from "../../Api/Client.js";
import InfoTooltip from "../../Shared/InfoTooltip.jsx";
import { Panel } from "../../Shared/ScreenComponents.jsx";
import {
  bundleSummary, cardOutcome, decisionError, groupByBundle, parseHours, readableFacts, timeAgo,
} from "./proposalsViewModel.js";
import "./ProposalsScreen.css";

/* Suggested Tasks: what the AI helpers suggest, for a person to approve or reject.
 *
 * Each card is one suggestion (a new task, a status change, an end-of-day report) already
 * written in plain words by the server, and the server lists only the ones this person may decide.
 * On Approve the server does the work itself. The decision always carries the signed-in person,
 * decided on the server, so this screen never sends who is approving. Handled cards fold into a greyed one-line summary in place; nothing else moves.
 * Helper text is shown as plain text only.
 *
 * A helper can send several suggestions as one bundle (a sprint plan's tasks). They show as one group with
 * Approve all and Reject all; every card in it can still be decided on its own. The group asks the server
 * to decide exactly the cards that were waiting on screen, and the server answers for each card, so a card
 * that could not be created says so and the rest stand.
 */

const BASE = "/axon/proposals";
const NOT_WAITING = new Set(["not_found", "already_handled"]);

function Fact({ fact }) {
  return (
    <div className={`Proposal-Fact${fact.wide ? " Wide" : ""}`}>
      <dt>{fact.label}</dt>
      <dd>
        {fact.items.length > 0
          ? <ul>{fact.items.map((text) => <li key={text}>{text}</li>)}</ul>
          : fact.value}
      </dd>
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="Proposal-List" aria-busy="true" aria-label="Loading suggested tasks">
      {[0, 1, 2].map((n) => (
        <div className="Proposal-Card Proposal-Skeleton" key={n}>
          <span className="Skel-Line Wide" />
          <span className="Skel-Line" />
          <span className="Skel-Line Short" />
        </div>
      ))}
    </div>
  );
}

function ProposalCard({ item, outcome, onOutcome }) {
  const [mode, setMode] = useState("idle"); // idle | hours | reject
  const [hours, setHours] = useState(item.estimated_hours == null ? "" : String(item.estimated_hours));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [problem, setProblem] = useState("");

  if (outcome) {
    return (
      <article className={`Proposal-Card Proposal-Done ${outcome.kind}`} aria-live="polite">
        {outcome.kind === "Failed" ? <AlertCircle size={16} strokeWidth={2.2} /> : <Check size={16} strokeWidth={2.2} />}
        <span className="Proposal-Done-Text">
          <strong>{item.title}</strong> · {outcome.text}
        </span>
      </article>
    );
  }

  const decide = async (verb) => {
    setProblem("");
    const body = {};
    if (verb === "approve" && mode === "hours") {
      const parsed = parseHours(hours);
      if (!parsed.ok) { setProblem(parsed.error); return; }
      if (parsed.value !== null && parsed.value !== item.estimated_hours) body.edited_hours = parsed.value;
    }
    if (verb === "reject" && note.trim()) body.note = note.trim();
    setBusy(verb);
    try {
      const answer = await apiPost(`${BASE}/${encodeURIComponent(item.id)}/${verb}/`, body);
      const done = verb === "approve" ? "Approved" : "Rejected";
      const text = verb === "approve" && item.kind === "task.create" && answer?.task_id
        ? `Approved · task #${answer.task_id} created` : done;
      onOutcome(item.id, { kind: done, text });
    } catch (error) {
      const gone = NOT_WAITING.has(error?.payload?.code);
      if (gone) onOutcome(item.id, { kind: "Gone", text: decisionError(error) });
      else setProblem(decisionError(error));
    } finally {
      setBusy("");
    }
  };

  const ago = timeAgo(item.created_at);
  const from = item.helper_label;
  const working = Boolean(busy);

  return (
    <article className="Proposal-Card">
      <header className="Proposal-Head">
        <div>
          <span className="Proposal-Kind">{item.kind_label}</span>
          <h3>{item.title || "Untitled suggestion"}</h3>
        </div>
        <InfoTooltip title="AI reasoning" width={300}>
          <strong>Why you are seeing this.</strong> {from} suggested this
          {item.evidence_summary ? `: ${item.evidence_summary}` : "."} Nothing changes in anyone's work until you approve it.
        </InfoTooltip>
      </header>

      {item.description && <p className="Proposal-Description">{item.description}</p>}

      <dl className="Proposal-Facts">
        {readableFacts(item.facts, item.title).map((fact) => <Fact key={fact.label} fact={fact} />)}
      </dl>

      <p className="Proposal-Source">
        <Sparkles size={13} /> From {from}{ago ? ` · ${ago}` : ""}
      </p>

      {mode === "hours" && (
        <div className="Proposal-Edit">
          <label htmlFor={`hours-${item.id}`}>Hours for this task</label>
          <input
            id={`hours-${item.id}`} type="text" inputMode="decimal" value={hours} disabled={working}
            onChange={(event) => setHours(event.target.value)}
          />
        </div>
      )}
      {mode === "reject" && (
        <div className="Proposal-Edit">
          <label htmlFor={`note-${item.id}`}>Add a note (optional)</label>
          <textarea
            id={`note-${item.id}`} rows={2} maxLength={1000} value={note} disabled={working}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      )}

      {problem && <p className="Proposal-Problem" role="alert">{problem}</p>}

      <div className="Proposal-Actions">
        {mode !== "reject" && (
          <>
            <button type="button" className="Primary-Button" disabled={working} onClick={() => decide("approve")}>
              {busy === "approve" ? "Approving…" : "Approve"}
            </button>
            <button type="button" className="Soft-Button" disabled={working} onClick={() => { setProblem(""); setMode("reject"); }}>
              Reject
            </button>
            {mode === "idle" && item.editable_hours && (
              <button type="button" className="Proposal-Link" disabled={working} onClick={() => { setProblem(""); setMode("hours"); }}>
                Change hours
              </button>
            )}
          </>
        )}
        {mode === "reject" && (
          <>
            <button type="button" className="Soft-Button danger" disabled={working} onClick={() => decide("reject")}>
              {busy === "reject" ? "Rejecting…" : "Reject"}
            </button>
            <button type="button" className="Soft-Button" disabled={working} onClick={() => { setProblem(""); setMode("idle"); }}>
              Keep it
            </button>
          </>
        )}
      </div>
    </article>
  );
}

function BundleGroup({ block, outcomes, onOutcomes }) {
  const [confirm, setConfirm] = useState(""); // "" | approve | reject
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [summary, setSummary] = useState(null); // { text, failed }

  const waiting = block.items.filter((item) => !outcomes[item.id]);

  const decideAll = async (verb) => {
    setProblem("");
    setBusy(true);
    try {
      const answer = await apiPost(`${BASE}/bundle/${verb}/`, {
        helper: block.helper, bundle_id: block.bundleId, ids: waiting.map((item) => item.id),
      });
      onOutcomes(Object.fromEntries((answer.results || []).map((result) => [result.id, cardOutcome(verb, result)])));
      setSummary({ text: bundleSummary(verb, answer), failed: answer.failed > 0 });
      setConfirm("");
    } catch (error) {
      setProblem(decisionError(error));
    } finally {
      setBusy(false);
    }
  };

  const how = waiting.length === 1 ? "1 task" : `${waiting.length} tasks`;

  return (
    <section className="Proposal-Bundle" aria-label={block.title}>
      <header className="Proposal-Bundle-Head">
        <div>
          <span className="Proposal-Kind"><Layers size={12} /> Suggested together</span>
          <h2>{block.title}</h2>
          <p>{waiting.length > 0 ? `${how} waiting` : "All handled"}</p>
        </div>
        {waiting.length > 0 && (
          <div className="Proposal-Bundle-Actions">
            {confirm === "" && (
              <>
                <button type="button" className="Primary-Button" disabled={busy} onClick={() => { setProblem(""); setConfirm("approve"); }}>
                  Approve all
                </button>
                <button type="button" className="Soft-Button" disabled={busy} onClick={() => { setProblem(""); setConfirm("reject"); }}>
                  Reject all
                </button>
              </>
            )}
            {confirm !== "" && (
              <>
                <span className="Proposal-Bundle-Ask">
                  {confirm === "approve" ? `Create ${how}?` : `Reject ${how}?`}
                </span>
                <button
                  type="button" disabled={busy} onClick={() => decideAll(confirm)}
                  className={confirm === "approve" ? "Primary-Button" : "Soft-Button danger"}
                >
                  {busy ? "Working…" : confirm === "approve" ? "Yes, approve all" : "Yes, reject all"}
                </button>
                <button type="button" className="Soft-Button" disabled={busy} onClick={() => { setProblem(""); setConfirm(""); }}>
                  Cancel
                </button>
              </>
            )}
          </div>
        )}
      </header>
      {problem && <p className="Proposal-Problem" role="alert">{problem}</p>}
      {summary && (
        <p className={`Proposal-Bundle-Note${summary.failed ? " Failed" : ""}`} role="status">
          {summary.text}{summary.failed ? " The ones that did not go through are marked below." : ""}
        </p>
      )}
      <div className="Proposal-List">
        {block.items.map((item) => (
          <ProposalCard
            key={item.id} item={item}
            outcome={outcomes[item.id]} onOutcome={(id, outcome) => onOutcomes({ [id]: outcome })}
          />
        ))}
      </div>
    </section>
  );
}

export function ProposalsScreen() {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [items, setItems] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [problem, setProblem] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    setProblem("");
    try {
      const payload = await apiGet(`${BASE}/`);
      setItems(Array.isArray(payload?.suggestions) ? payload.suggestions : []);
      setOutcomes({});
      setState("ready");
    } catch (error) {
      setProblem(decisionError(error));
      setState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Outcomes arrive as { cardId: outcome }, one card or a whole bundle at a time.
  const addOutcomes = useCallback((added) => setOutcomes((current) => ({ ...current, ...added })), []);
  const setOutcome = useCallback((id, outcome) => addOutcomes({ [id]: outcome }), [addOutcomes]);
  const waiting = items.filter((item) => !outcomes[item.id]).length;
  const blocks = useMemo(() => groupByBundle(items), [items]);

  return (
    <section className="Proposals-Screen Screen-Stack">
      <header className="ds-page-header">
        <div>
          <span className="ds-page-header__eyebrow">AI helpers</span>
          <h1 className="ds-page-header__title"><Inbox size={22} strokeWidth={1.8} style={{ verticalAlign: "-3px" }} /> Suggested Tasks</h1>
          <p className="ds-page-header__sub">
            What your AI helpers suggest. Nothing changes until you approve it.
            {state === "ready" && waiting > 0 ? ` ${waiting} waiting.` : ""}
          </p>
        </div>
        <div className="ds-page-header__actions">
          <button type="button" className="Soft-Button Small" onClick={load} disabled={state === "loading"}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      {state === "loading" && <SkeletonCards />}

      {state === "error" && (
        <Panel title="We could not load the suggested tasks">
          <div className="Proposal-Error">
            <p>{problem}</p>
            <button type="button" className="Primary-Button Small" onClick={load}>
              <RefreshCw size={14} /> Try again
            </button>
          </div>
        </Panel>
      )}

      {state === "ready" && items.length === 0 && (
        <div className="Proposal-Empty">
          <Check size={22} strokeWidth={1.8} />
          <strong>Nothing waiting for you</strong>
        </div>
      )}

      {state === "ready" && items.length > 0 && (
        <div className="Proposal-List">
          {blocks.map((block) => (block.kind === "bundle" ? (
            <BundleGroup key={block.key} block={block} outcomes={outcomes} onOutcomes={addOutcomes} />
          ) : (
            <ProposalCard
              key={block.key} item={block.item}
              outcome={outcomes[block.item.id]} onOutcome={setOutcome}
            />
          )))}
        </div>
      )}
    </section>
  );
}
